import { userAuthApiService } from '../services/UserAuthApiService';

/**
 * CSRF Token Utility
 *
 * Provides centralized CSRF token management for Django backend integration.
 * Handles token extraction from cookies, proactive fetching, and error detection.
 */

/**
 * Extract CSRF token from browser cookies
 * @returns CSRF token string or null if not found
 */
export const getCsrfTokenFromCookie = (): string | null => {
  const cookieString = document.cookie;

  if (!cookieString) {
    return null;
  }

  const cookies = cookieString
    .split('; ')
    .reduce((acc: Record<string, string>, current) => {
      const separatorIndex = current.indexOf('=');
      if (separatorIndex > 0) {
        const key = current.slice(0, separatorIndex).trim();
        const value = current.slice(separatorIndex + 1);
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, string>);

  return cookies.csrftoken || null;
};

/**
 * Fetch a fresh CSRF token from the backend
 * Makes a GET request to /auth/csrf/ which triggers Django to set the csrftoken cookie
 *
 * @returns Promise resolving to CSRF token string or null if fetch fails
 */
export const fetchCsrfToken = async (): Promise<string | null> => {
  try {
    // Use service to fetch token
    const headerToken = await userAuthApiService.getCsrfToken();

    if (headerToken) {
      return headerToken;
    }

    // Fallback: extract from cookie after backend sets it
    const cookieToken = getCsrfTokenFromCookie();

    if (!cookieToken) {
      console.warn(
        'CSRF token fetch succeeded but token not found in cookie or header. ' +
          'This may indicate a backend configuration issue.'
      );
    }

    return cookieToken;
  } catch (error) {
    // Graceful degradation - log warning but don't block user flow
    console.warn(
      'Failed to fetch CSRF token from /auth/csrf/. ' +
        'Login may fail if backend requires CSRF protection.',
      error
    );
    return null;
  }
};

/**
 * Promise queue to prevent concurrent CSRF token fetches
 * Ensures only one token fetch happens at a time
 */
let csrfTokenFetchPromise: Promise<string | null> | null = null;

/**
 * Fetch CSRF token with queue management
 * If a fetch is already in progress, returns the existing promise
 * Prevents race conditions and duplicate requests
 *
 * @returns Promise resolving to CSRF token string or null
 */
export const fetchCsrfTokenQueued = async (): Promise<string | null> => {
  if (csrfTokenFetchPromise) {
    // Return existing promise if fetch already in progress
    return csrfTokenFetchPromise;
  }

  // Start new fetch and store promise
  csrfTokenFetchPromise = fetchCsrfToken().finally(() => {
    // Clear promise when complete
    csrfTokenFetchPromise = null;
  });

  return csrfTokenFetchPromise;
};
